import React, { memo, useState, useMemo, useRef, useCallback, useEffect, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import * as ServerListActions from 'actions/ServerList';
import Context from 'managers/Context';
import { PATH_IMAGE } from 'constants/Environment';
import { getLiteral } from 'utils/getLiteral';
import { getFMAddressFromCoordinates, getFMAddressFromGeoCodeResults } from 'utils/map';
import {
    formatAddress,
    formatAddressFromGeocoder,
    shouldFormatAddressAsAmericanIso,
} from 'utils/addresses';
import ModalHoi from 'components/ModalHoi';
import SelectHoi from 'components/SelectHoi';
import Map from 'components/Map';
import { Button, RadioGroup, Text } from '@Sage/hoi-poi-ui';
import FieldWrapper from '../FieldWrapper';

import GeoLocationFieldMarker from './GeoLocationFieldMarker';
import './styles.scss';

const reducer = (state, action) => {
    switch (action.type) {
        case 'SET':
            return { ...state, [action.key]: action.payload };
        case 'SET_MULTIPLE':
            return { ...state, ...action.payload };
        case 'RESET_SELECT_LOCATION':
            return { ...state, locationValue: null, isPositionUpdated: false };
        default:
            return state;
    }
};

// Helper to create SET action
const set = (key, payload) => ({ type: 'SET', key, payload });
const setMultiple = (payload) => ({ type: 'SET_MULTIPLE', payload });

const GeoLocationField = ({
    // Standard field props
    label,
    labelMode = 'horizontal',
    mandatory,
    readOnly,
    hidden,
    description,
    isBulkAction,
    // Data props
    data,
    onChange,
    // inputAttrs - name of the ids of these fields
    latitude,
    longitude,
    isGeoLocated,
    address1,
    address2,
    city,
    province,
    cp,
    country,
    idCountry,
    mapMarkerIcon,
    // Additional props
    showAddress,
    notGeolocatedLabel,
    inputs,
    isNewEntity,
    configMap,
    smartFieldOverridesCache,
}) => {
    const dispatch = useDispatch();
    const countriesList = useSelector((state) => state.serverList?.tblCountries?.data);

    // ========================================
    // LOCAL STATE
    // ========================================
    const geolocationModalRef = useRef();
    const mapComponentRef = useRef(null);
    const markerComponentRef = useRef(null);
    const [locationInput, setLocationInput] = useState('');
    const [defaultOptions, setDefaultOptions] = useState([]);
    const [state, dispatchLocal] = useReducer(reducer, {
        isGeoLocationModalOpen: false,
        isConfirmChangeAddressModalOpen: false,
        isPositionUpdated: false,
        initialPositionMarker: null,
        positionMarker: null,
        initialFullAddress: null,
        addressFields: null,
        fullAddress: null,
        radioConfirmChangeValue: 'replace',
        listCountries: null,
        haveGeoLocation: null,
        locationValue: null,
    });

    // ========================================
    // DERIVED STATE & MEMOIZATION
    // ========================================
    const dataLatitude = data?.[latitude];
    const dataLongitude = data?.[longitude];

    const inputsSmartFieldProps = useMemo(() => {
        if (!inputs || !smartFieldOverridesCache) return {};

        const propsArray = inputs.map((field) => {
            return smartFieldOverridesCache?.get(field.id) || {};
        });

        return propsArray.reduce((acc, curr) => {
            Object.keys(curr).forEach((key) => {
                if (typeof curr[key] === 'boolean') {
                    acc[key] = acc[key] !== undefined ? acc[key] && curr[key] : curr[key];
                }
            });
            return acc;
        }, {});
    }, [inputs, smartFieldOverridesCache]);

    const finalMandatory = useMemo(
        () => inputsSmartFieldProps?.mandatory || mandatory,
        [inputsSmartFieldProps?.mandatory, mandatory],
    );

    const finalReadOnly = useMemo(
        () => inputsSmartFieldProps?.readOnly || readOnly,
        [inputsSmartFieldProps?.readOnly, readOnly],
    );

    const finalHidden = useMemo(
        () => inputsSmartFieldProps?.hidden || hidden,
        [inputsSmartFieldProps?.hidden, hidden],
    );

    const mapCenter = useMemo(() => {
        if (data?.[isGeoLocated]) {
            return { lat: dataLatitude, lng: dataLongitude };
        }
        return null;
    }, [dataLatitude, dataLongitude, data, isGeoLocated]);

    const changeAddressItems = useMemo(() => {
        return [
            { label: state.fullAddress, value: 'replace' },
            { label: state.initialFullAddress, value: 'keep' },
        ];
    }, [state.fullAddress, state.initialFullAddress]);

    // ========================================
    // EVENT HANDLERS & CALLBACKS
    // ========================================
    const loadOptions = useCallback((value) => {
        return Context.entityMapManager.autocomplete({ address: value }).then((data) => {
            return (
                data?.Result?.map((item) => ({
                    label: item.name,
                    value: item.address,
                    placeId: item.place_id,
                })) || []
            );
        });
    }, []);

    const onUpdateLocation = useCallback(
        ({ lat, lng, address }, firstLoad = false) => {
            getFMAddressFromCoordinates({ lat, lng }, address)
                .then((result) => {
                    const locationName = result?.locationName || result?.googleLocationName || null;

                    if (locationName) {
                        dispatchLocal(
                            setMultiple({
                                fullAddress: locationName,
                                addressFields: result,
                                positionMarker: { lat, lng },
                                ...(!state.isPositionUpdated &&
                                    !firstLoad && { isPositionUpdated: true }),
                            }),
                        );
                    } else {
                        dispatchLocal(
                            setMultiple({
                                fullAddress: null,
                                addressFields: null,
                                positionMarker: { lat, lng },
                                ...(!state.isPositionUpdated &&
                                    !firstLoad && { isPositionUpdated: true }),
                            }),
                        );
                    }
                })
                .catch(() => {
                    console.warn('error geocoding location');
                    dispatchLocal(setMultiple({ fullAddress: null, addressFields: null }));
                });
        },
        [state.isPositionUpdated],
    );

    const openModalMap = useCallback(() => {
        dispatchLocal(set('isGeoLocationModalOpen', true));
        dispatchLocal({ type: 'RESET_SELECT_LOCATION' });
        setLocationInput(data?.[address1] || '');
        loadOptions(data?.[address1] || '').then((options) => {
            setDefaultOptions(options);
        });
    }, [data, address1, loadOptions]);

    const closeModalMap = useCallback(() => {
        dispatchLocal(set('isGeoLocationModalOpen', false));
    }, []);

    const closeModalConfirmChange = useCallback(() => {
        dispatchLocal(
            setMultiple({
                isGeoLocationModalOpen: true,
                isConfirmChangeAddressModalOpen: false,
                radioConfirmChangeValue: 'replace',
            }),
        );
    }, []);

    const onSaveLocationModal = useCallback(
        (fullSave) => {
            if (state.positionMarker) {
                onChange(latitude)(state.positionMarker.lat);
                onChange(longitude)(state.positionMarker.lng);

                if (fullSave && state.addressFields) {
                    const americanFormat = shouldFormatAddressAsAmericanIso(
                        state.addressFields.countryShort,
                    );

                    let address1Value = state.addressFields.shortAddress;
                    if (state.addressFields.streetNumber) {
                        address1Value = formatAddressFromGeocoder(
                            state.addressFields.shortAddress,
                            state.addressFields.streetNumber,
                            '',
                            '',
                            '',
                            '',
                            '',
                            state.addressFields.countryShort,
                            '',
                        );

                        if (state.addressFields.subpremise)
                            address1Value = `${address1Value} #${state.addressFields.subpremise}`;
                    }

                    let address2Value = '';
                    if (state.addressFields.administrativeAreaLevel3 && !americanFormat) {
                        address2Value = state.addressFields.administrativeAreaLevel3;
                    }

                    const cityValue = state.addressFields.locality || '';
                    const provinceValue =
                        state.addressFields.province ??
                        (americanFormat
                            ? state.addressFields.state || ''
                            : state.addressFields.region || '');
                    const cpValue = state.addressFields.postalCode || '';

                    let idCountryValue = '';
                    if (state.addressFields.countryShort && state.listCountries) {
                        const foundCountry = state.listCountries.find((item) => {
                            const countryShort = state.addressFields.countryShort.toLowerCase();
                            return (
                                item.striso2?.toLowerCase() === countryShort ||
                                item.striso3?.toLowerCase() === countryShort
                            );
                        });
                        if (foundCountry) idCountryValue = foundCountry.value;
                    }

                    let countryValue = '';
                    if (state.addressFields.country) countryValue = state.addressFields.country;

                    onChange(address1)(address1Value);
                    onChange(address2)(address2Value);
                    onChange(city)(cityValue);
                    onChange(province)(provinceValue);
                    onChange(cp)(cpValue);
                    if (idCountry) onChange(idCountry)(idCountryValue);
                    if (country) onChange(country)(countryValue);
                }
                dispatchLocal(set('haveGeoLocation', true));
            }

            dispatchLocal(
                setMultiple({
                    isConfirmChangeAddressModalOpen: false,
                    isGeoLocationModalOpen: false,
                }),
            );
        },
        [
            state.positionMarker,
            state.addressFields,
            state.listCountries,
            onChange,
            latitude,
            longitude,
            address1,
            address2,
            city,
            province,
            cp,
            idCountry,
            country,
        ],
    );

    const onSaveGeoLocation = useCallback(() => {
        if (
            state.isPositionUpdated &&
            state.initialFullAddress &&
            state.initialFullAddress !== state.fullAddress
        ) {
            dispatchLocal(set('isConfirmChangeAddressModalOpen', true));
        } else {
            onSaveLocationModal(true);
        }
    }, [state.isPositionUpdated, state.initialFullAddress, state.fullAddress, onSaveLocationModal]);

    const onSaveConfirmChangeAddress = useCallback(() => {
        onSaveLocationModal(state.radioConfirmChangeValue === 'replace');
        dispatchLocal(set('radioConfirmChangeValue', 'replace'));
    }, [onSaveLocationModal, state.radioConfirmChangeValue]);

    const onChangeRadioConfirmChangeAddress = useCallback((value) => {
        dispatchLocal(set('radioConfirmChangeValue', value));
    }, []);

    const onAutoCompleteSearch = useCallback(() => {
        const mapRef = mapComponentRef.current;
        if (mapRef?.getCenter) {
            const center = mapRef.getCenter();
            const markerRef = markerComponentRef.current;
            if (markerRef?.setMarkerPosition) {
                const position = { lat: center?.lat, lng: center?.lng };
                markerRef.setMarkerPosition(position);
                onUpdateLocation(position);
            }
        }
    }, [onUpdateLocation]);

    const onMapClick = useCallback(
        (event) => {
            if (!event.latLng) return;

            const position = { lat: event.latLng.lat(), lng: event.latLng.lng() };
            markerComponentRef.current?.setMarkerPosition?.(position);
            onUpdateLocation(position);
        },
        [onUpdateLocation],
    );

    const onLoadMap = useCallback(() => {
        const lat = dataLatitude;
        const lng = dataLongitude;
        const mapRef = mapComponentRef.current;

        if (lat && lng) {
            dispatchLocal(
                setMultiple({
                    initialPositionMarker: { lat, lng },
                    initialFullAddress: formatAddress(
                        data?.[address1],
                        data?.[address2],
                        data?.[city],
                        data?.[province],
                        data?.[cp],
                        data?.[country],
                        data?.[idCountry],
                    ),
                }),
            );
            onUpdateLocation({ lat, lng }, true);
            mapRef?.setCenter({ lat, lng });
        } else if (mapRef?.getCenter) {
            const center = mapRef.getCenter();
            const position = { lat: center?.lat, lng: center?.lng };
            dispatchLocal(set('initialPositionMarker', position));
            onUpdateLocation(position, true);
        }
    }, [
        dataLatitude,
        dataLongitude,
        onUpdateLocation,
        data,
        address1,
        address2,
        city,
        province,
        cp,
        country,
        idCountry,
    ]);

    const onChangeLocation = useCallback(
        (item) => {
            dispatchLocal(set('locationValue', item));
            setTimeout(() => {
                setLocationInput(item?.label || '');
            });
            if (!item) return;

            Context.entityMapManager.getPlaceDetails({ placeId: item.placeId }).then((data) => {
                const location = {
                    lat: data.Result?.FMGeoCodingResults?.[0]?.FMGeometry?.FMLocation?.FMLat,
                    lng: data.Result?.FMGeoCodingResults?.[0]?.FMGeometry?.FMLocation?.FMLng,
                };

                if (data.Result) {
                    const mapRef = mapComponentRef.current;
                    const markerRef = markerComponentRef.current;

                    mapRef?.setCenter(location);
                    markerRef?.setMarkerPosition?.(location);

                    const result = getFMAddressFromGeoCodeResults(data);
                    const locationName = result?.locationName || result?.googleLocationName || null;
                    dispatchLocal(
                        setMultiple({
                            fullAddress: locationName,
                            addressFields: locationName ? result : null,
                            positionMarker: location,
                            ...(!state.isPositionUpdated && { isPositionUpdated: true }),
                        }),
                    );
                }
            });
        },
        [state.isPositionUpdated],
    );

    // ========================================
    // EFFECTS
    // ========================================
    useEffect(() => {
        dispatch(ServerListActions.getList('tblCountries')).then((data) => {
            dispatchLocal(set('listCountries', data));
        });
    }, [dispatch]);

    // ========================================
    // MEMOIZED COMPONENTS
    // ========================================
    const mapImage = useMemo(() => {
        const isLocated = data?.[isGeoLocated] || state.haveGeoLocation;

        if (isLocated) {
            let dataFullAddress = '';
            if (data?.[address1]) dataFullAddress = data[address1];
            if (data?.[cp]) dataFullAddress = `${dataFullAddress}, ${data[cp]}`;
            if (data?.[city]) dataFullAddress = `${dataFullAddress} ${data[city]}`;
            if (data?.[province]) dataFullAddress = `${dataFullAddress}, ${data[province]}`;
            if (data?.[country]) dataFullAddress = `${dataFullAddress}, ${data[country]}`;

            const finalAddress = state.fullAddress || dataFullAddress;

            const geoMapClassName = classnames(
                'fm-field-geolocation__map fm-field-geolocation__map-geo-located',
                { 'fm-field-geolocation__map--read-only': finalReadOnly },
            );

            return (
                <>
                    <div
                        className={geoMapClassName}
                        onClick={!finalReadOnly ? openModalMap : undefined}
                    >
                        <img
                            className="fm-field-geolocation__map__image"
                            src={`${PATH_IMAGE}img-map-on.svg`}
                            alt=""
                        />
                        <img
                            className="fm-field-geolocation__map__image-marker"
                            src={`${PATH_IMAGE}img-pin-on.svg`}
                            alt=""
                        />
                        <Button
                            className="fm-field-geolocation__map__button"
                            size="small"
                            isDisabled={finalReadOnly}
                        >
                            {getLiteral('action_change_location')}
                        </Button>
                    </div>
                    {showAddress && finalAddress && (
                        <div className="fm-field-geolocation__map-address-footer">
                            <Text type="caption">{finalAddress}</Text>
                        </div>
                    )}
                </>
            );
        }

        const geoMapClassName = classnames(
            'fm-field-geolocation__map fm-field-geolocation__map-not-geo-located',
            { 'fm-field-geolocation__map--read-only': finalReadOnly },
        );

        const addressCaption = data?.[address1] || notGeolocatedLabel;

        return (
            <>
                <div
                    className={geoMapClassName}
                    onClick={!finalReadOnly ? openModalMap : undefined}
                >
                    <img
                        className="fm-field-geolocation__map__image"
                        src={`${PATH_IMAGE}img-map-off.svg`}
                        alt=""
                    />
                    <img
                        className="fm-field-geolocation__map__image-marker"
                        src={`${PATH_IMAGE}img-pin-off.svg`}
                        alt=""
                    />
                    <Button
                        className="fm-field-geolocation__map__button"
                        size="small"
                        isDisabled={finalReadOnly}
                    >
                        {getLiteral('action_mapsearch')}
                    </Button>
                </div>
                {showAddress && addressCaption && (
                    <div className="fm-field-geolocation__map-address-footer">
                        <Text type="caption">{addressCaption}</Text>
                    </div>
                )}
            </>
        );
    }, [
        data,
        isGeoLocated,
        state.haveGeoLocation,
        state.fullAddress,
        address1,
        cp,
        city,
        province,
        country,
        finalReadOnly,
        openModalMap,
        showAddress,
        notGeolocatedLabel,
    ]);

    const overridesModal = useMemo(
        () => ({
            root: {
                contentRef: (r) => (geolocationModalRef.current = r),
            },
        }),
        [],
    );

    const overridesSelect = useMemo(() => {
        return {
            'react-select': {
                filterOption: false,
                inputValue: locationInput || '',
                openMenuOnClick: true,
                defaultOptions,
                defaultMenuIsOpen: !state.isPositionUpdated && !!locationInput,
                onInputChange: (text, { action }) => {
                    if (
                        action === 'set-value' ||
                        action === 'input-blur' ||
                        action === 'menu-close'
                    ) {
                        return;
                    }
                    setLocationInput(text);
                },
            },
        };
    }, [defaultOptions, locationInput, state.isPositionUpdated]);

    const modalClasses = useMemo(() => ({ content: 'fm-field-geolocation__modal-content' }), []);

    // ========================================
    // RENDER
    // ========================================
    if (finalHidden) return null;

    const mapModalClasses = classnames('fm-field-geolocation__modal-map', {
        'fm-field-geolocation__modal-map-hide': state.isConfirmChangeAddressModalOpen,
    });

    return (
        <>
            <FieldWrapper
                className="fm-field-geolocation"
                label={
                    label || (labelMode === 'horizontal' ? <span aria-hidden="true" /> : undefined)
                }
                labelMode={labelMode}
                mandatory={finalMandatory}
                description={description}
                isBulkAction={isBulkAction}
            >
                <div className="fm-field-geolocation__content">{mapImage}</div>
            </FieldWrapper>

            {state.isGeoLocationModalOpen && (
                <ModalHoi
                    useHeader
                    title={getLiteral('label_set_geolocation')}
                    isOpen={state.isGeoLocationModalOpen}
                    width="60vw"
                    className={mapModalClasses}
                    cancelText={getLiteral('action_cancel')}
                    onCancel={closeModalMap}
                    confirmText={getLiteral('action_save')}
                    onConfirm={onSaveGeoLocation}
                    onRequestClose={closeModalMap}
                    overlayClassName="fm-field-geolocation__modal-map__overlay"
                    shouldCloseOnOverlayClick={false}
                    isConfirmDisabled={!state.isPositionUpdated}
                    classes={modalClasses}
                    overrides={overridesModal}
                >
                    <SelectHoi
                        placeholder={getLiteral('label_location')}
                        isClearable
                        value={state.locationValue}
                        loadOptions={loadOptions}
                        onChange={onChangeLocation}
                        overrides={overridesSelect}
                        isFuzzy
                        className="fm-field-geolocation__modal-map__select"
                        isFullWidth
                    />
                    <div className="fm-field-geolocation__modal-map__content">
                        <Map
                            ref={mapComponentRef}
                            onLoadMap={onLoadMap}
                            center={mapCenter}
                            zoom={17}
                            onAutoCompleteSearch={onAutoCompleteSearch}
                            onMapClick={onMapClick}
                        >
                            <GeoLocationFieldMarker
                                ref={markerComponentRef}
                                position={state.initialPositionMarker}
                                icon={mapMarkerIcon}
                                updateLocation={onUpdateLocation}
                            />
                        </Map>
                        {state.fullAddress && (
                            <Text
                                as="div"
                                className="fm-field-geolocation__modal-map__address"
                                type="caption"
                            >
                                {state.fullAddress}
                            </Text>
                        )}
                    </div>
                </ModalHoi>
            )}

            {state.isConfirmChangeAddressModalOpen && (
                <ModalHoi
                    isOpen={state.isConfirmChangeAddressModalOpen}
                    width="600px"
                    className="fm-field-geolocation__modal-confirm-address"
                    title={getLiteral('title_confirm_location')}
                    confirmText={getLiteral('action_accept')}
                    cancelText={getLiteral('action_cancel')}
                    onCancel={closeModalConfirmChange}
                    onConfirm={onSaveConfirmChangeAddress}
                    onRequestClose={closeModalConfirmChange}
                    overlayClassName="fm-field-geolocation__modal-map__overlay"
                    shouldCloseOnOverlayClick={false}
                >
                    <div className="fm-field-geolocation__modal-confirm-address__title">
                        {getLiteral('title_change_direction')}
                    </div>
                    <RadioGroup
                        label={getLiteral('label_replace_with')}
                        labelMode="vertical"
                        options={changeAddressItems}
                        onChange={onChangeRadioConfirmChangeAddress}
                        value={state.radioConfirmChangeValue}
                        isFullWidth
                    />
                </ModalHoi>
            )}
        </>
    );
};

GeoLocationField.displayName = 'GeoLocationField';

GeoLocationField.propTypes = {
    label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    labelMode: PropTypes.string,
    mandatory: PropTypes.bool,
    readOnly: PropTypes.bool,
    hidden: PropTypes.bool,
    description: PropTypes.string,
    isBulkAction: PropTypes.bool,
    data: PropTypes.object,
    onChange: PropTypes.func,
    latitude: PropTypes.string,
    longitude: PropTypes.string,
    isGeoLocated: PropTypes.string,
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    province: PropTypes.string,
    cp: PropTypes.string,
    country: PropTypes.string,
    idCountry: PropTypes.string,
    mapMarkerIcon: PropTypes.node,
    showAddress: PropTypes.bool,
    notGeolocatedLabel: PropTypes.string,
    inputs: PropTypes.array,
    isNewEntity: PropTypes.bool,
    configMap: PropTypes.object,
    smartFieldOverridesCache: PropTypes.object,
};

export default memo(GeoLocationField);
